import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { dailyWeatherPageTestIDs } from '@constants';
import { useGetUserLocation } from '@hooks';
import { DailyForecastData, dayDateTimeState } from '@store';

import { styles } from './styles';

import { TimeUtils } from '../../../../helpers/TimeUtils';
import { TWCAccessibleContent } from '../../../focusable/TWCAccessibleContent/TWCAccessibleContent';
import { TWCText } from '../../../primitives/TWCText/TWCText';

export type DailyHeaderProps = {
  data: Pick<DailyForecastData, 'validTimeUtc'>;
  index: number;
  nextFocusDown: number | null;
  defaultFocus: number | null;
};

export const DailyHeader: FC<DailyHeaderProps> = ({ data, index, nextFocusDown, defaultFocus }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'dailyWeatherPage.dailyHeader',
  });

  const { timeZone } = useRecoilValue(dayDateTimeState);

  let dailyDate: Date | undefined;

  if (data?.validTimeUtc?.[index]) {
    dailyDate = TimeUtils.dateFromEpoch(data.validTimeUtc[index]);
  }
  const date = TimeUtils.getWeekDayMonthDate(dailyDate ?? Date.prototype.getCurrent(), timeZone);

  const { city } = useGetUserLocation() || {};

  return (
    <TWCAccessibleContent
      accessibilityHint={t('accessibilityHint')}
      accessibilityLabel={t('accessibilityLabel', {
        city,
        date,
      })}
      disabledScreenreaderFocus={defaultFocus}
      nextFocusDown={nextFocusDown}
      testID={dailyWeatherPageTestIDs.dailyWeatherTopRow}
    >
      <View importantForAccessibility="no-hide-descendants" style={styles.container}>
        <View style={styles.topRow}>
          <TWCText testID={dailyWeatherPageTestIDs.dailyHeaderLabel} variant="pageHeader">
            {t('header')}
          </TWCText>
        </View>
        <View style={styles.bottomRow}>
          <TWCText testID={dailyWeatherPageTestIDs.dailyHeaderLocation} variant="regularText">
            {city}
          </TWCText>
          <View style={styles.divider} />
          <TWCText testID={dailyWeatherPageTestIDs.dailyHeaderTimeStamp} variant="regularText">
            {date}
          </TWCText>
        </View>
      </View>
    </TWCAccessibleContent>
  );
};
